import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { ServicePlan } from 'src/features/servicePlans/utils/types';
import { useDeleteServicePlan } from 'src/features/servicePlans/api/useDeleteServicePlan';
import { LIST_SERVICE_PLANS_RESOURCE } from 'src/features/servicePlans/api/useListServicePlans';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useOnError } from 'src/utils/errors';

interface DeleteServicePlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlan: ServicePlan;
    children?: React.ReactNode;
}

function DeleteServicePlanFormModal<TData>({ selectedServicePlan, children }: DeleteServicePlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const onError = useOnError();

    const { mutate: deletePayStation } = useDeleteServicePlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLANS_RESOURCE] });
            setIsModalOpen(false);
            navigate(`/service-plans`);
            toast({
                title: formatMessage(servicePlansMessages.deleteServicePlanSuccess),
                description: formatMessage(servicePlansMessages.deleteServicePlanSuccessDescription),
            });
        },
        onError: (error) => onError(error, () => undefined),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlansMessages.deleteServicePlan },
                subTitle: {
                    ...servicePlansMessages.deleteServicePlanSubtitle,
                    values: {
                        entity: selectedServicePlan.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...servicePlansMessages.confirmDeleteServicePlan },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: servicePlansMessages.deleteServicePlan,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedServicePlan.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteServicePlanFormModal;
